<template>
  <main class="app-content">
    <!-- ══════════ MÀN 1: DANH SÁCH KỲ ĐỐI SOÁT ══════════ -->
    <template v-if="!currentId">
      <div class="app-title">
        <div>
          <h1><font-awesome-icon icon="balance-scale"/> {{ $t('pageTitle') }}</h1>
        </div>
        <div>
          <button class="btn btn-primary" @click="openCreate">
            <font-awesome-icon icon="plus"/>&nbsp;{{ $t('createPeriod') }}
          </button>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="tile">
            <div class="form-inline">
              <label class="mr-1 mb-1">{{ $t('provider') }}</label>
              <b-form-select class="mr-2 mb-1" v-model="filterGateway" :options="gatewayOptions"
                             @change="loadList" style="width:180px"></b-form-select>
              <label class="mr-1 mb-1">{{ $t('status') }}</label>
              <b-form-select class="mr-2 mb-1" v-model="filterStatus" :options="statusOptions"
                             @change="loadList" style="width:170px"></b-form-select>
              <button class="btn btn-secondary mr-1 mb-1" @click="loadList" :disabled="loading">
                <font-awesome-icon icon="sync" :spin="loading"/>&nbsp;{{ $t('reload') }}
              </button>
            </div>
            <b-alert show variant="info" class="small mt-2 mb-0">{{ $t('listNote') }}</b-alert>
          </div>
        </div>

        <div class="col-12">
          <div class="tile">
            <div class="text-center text-gray-999 py-4" v-if="loading">
              <b-spinner small></b-spinner>&nbsp;{{ $t('loading') }}
            </div>

            <div class="d-empty" v-else-if="!periods.length">
              <div class="d-empty__icon">⚖️</div>
              <p class="mb-1"><b>{{ $t('emptyTitle') }}</b></p>
              <p class="small text-gray-999">{{ $t('emptyHint') }}</p>
              <button class="btn btn-primary mt-2" @click="openCreate">
                <font-awesome-icon icon="plus"/>&nbsp;{{ $t('createPeriod') }}
              </button>
            </div>

            <div class="table-responsive" v-else>
              <table class="table table-hover">
                <thead>
                <tr>
                  <th>{{ $t('colPeriod') }}</th>
                  <th>{{ $t('colFile') }}</th>
                  <th>{{ $t('provider') }}</th>
                  <th class="text-right">{{ $t('colFileRows') }}</th>
                  <th class="text-right">{{ $t('colNeedRefund') }}</th>
                  <th class="text-right">{{ $t('colNeedCharge') }}</th>
                  <th>{{ $t('status') }}</th>
                  <th>{{ $t('colCreatedBy') }}</th>
                  <th>{{ $t('colNote') }}</th>
                  <th class="text-right">{{ $t('colActions') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="p in periods" :key="p._id" class="d-rowlink" @click="openPeriod(p._id)">
                  <td>
                    <b>{{ p.from | moment('DD/MM/YYYY') }} → {{ p.to | moment('DD/MM/YYYY') }}</b>
                    <div class="small text-gray-999">{{ p.createTime | moment('DD/MM HH:mm') }}</div>
                  </td>
                  <td class="small">{{ p.fileName || '—' }}</td>
                  <td>{{ p.gateway ? p.gateway.name : '—' }}</td>
                  <td class="text-right">{{ p.totals.fileRows | numFormat('0,0') }}</td>
                  <td class="text-right">
                    <span v-if="p.totals.providerFail" class="text-danger">
                      {{ p.totals.providerFail }} · {{ p.totals.refundAmount | numFormat('0,0') }}đ
                    </span>
                    <span v-else class="text-gray-999">—</span>
                  </td>
                  <td class="text-right">
                    <span v-if="p.totals.providerSuccess" class="text-warning">{{ p.totals.providerSuccess }}</span>
                    <span v-else class="text-gray-999">—</span>
                  </td>
                  <td>
                    <span class="badge" :class="p.status === 'applied' ? 'badge-success' : 'badge-warning'">
                      {{ p.status === 'applied' ? $t('applied') : $t('notApplied') }}
                    </span>
                  </td>
                  <td class="small">{{ p.importedBy ? p.importedBy.name : '—' }}</td>
                  <td class="small"><div class="text-truncate" style="max-width:220px" v-b-tooltip.hover :title="p.note">{{ p.note || '—' }}</div></td>
                  <td class="text-right text-nowrap" @click.stop>
                    <button class="btn btn-sm btn-primary mr-1" @click="openPeriod(p._id)">
                      {{ $t('openPeriod') }}
                    </button>
                    <button class="btn btn-sm btn-outline-danger is-border-1" @click="removePeriod(p)"
                            :disabled="deleting" :title="$t('deletePeriod')">
                      <font-awesome-icon icon="trash"/>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ══════════ MÀN 2: CHI TIẾT MỘT KỲ ══════════ -->
    <template v-else>
      <div class="app-title">
        <div>
          <h1>
            <a href="javascript:;" @click="backToList" class="d-back" :title="$t('backToList')">
              <font-awesome-icon icon="arrow-left"/>
            </a>
            {{ $t('pageTitle') }}
          </h1>
          <p class="small text-gray-999 mb-0" v-if="current">
            {{ current.fileName }} ·
            {{ current.from | moment('DD/MM/YYYY') }} → {{ current.to | moment('DD/MM/YYYY') }} ·
            {{ current.gateway ? current.gateway.name : '' }}
          </p>
        </div>
        <div v-if="current">
          <button class="btn btn-outline-info is-border-1 mr-1" @click="exportExcel" :disabled="exporting">
            <font-awesome-icon icon="file-excel"/>&nbsp;{{ $t('exportReport') }}
          </button>
          <button class="btn btn-outline-danger is-border-1" @click="removePeriod(current, true)" :disabled="deleting">
            <font-awesome-icon icon="trash"/>&nbsp;{{ $t('deletePeriod') }}
          </button>
        </div>
      </div>

      <div class="text-center text-gray-999 py-5" v-if="!current">
        <b-spinner small></b-spinner>&nbsp;{{ $t('loading') }}
      </div>

      <div class="row" v-else>
        <!-- Ba bước xử lý MỘT kỳ, trạng thái lấy từ dữ liệu thật chứ không phải trang trí -->
        <div class="col-12">
          <div class="d-steps">
            <div class="d-step done">
              <span>✓</span>{{ $t('stepMatched') }}
              <div class="small">{{ current.totals.fileRows | numFormat('0,0') }} {{ $t('rowsFromFile') }}</div>
            </div>
            <div class="d-step" :class="{on: needAction > 0, done: needAction === 0}">
              <span v-if="needAction === 0">✓</span><span v-else>2</span>{{ $t('stepPick') }}
              <div class="small">{{ needAction ? $t('stepPickHint', [needAction]) : $t('stepPickDone') }}</div>
            </div>
            <div class="d-step" :class="{on: needAction > 0 && selectedCount > 0, done: hasApplied && needAction === 0}">
              <span v-if="hasApplied && needAction === 0">✓</span><span v-else>3</span>{{ $t('stepApply') }}
              <div class="small">
                <template v-if="hasApplied">
                  {{ $t('stepApplyDone', [fmt(current.totals.appliedRefund), fmt(current.totals.appliedCharge)]) }}
                </template>
                <template v-else-if="needAction === 0">{{ $t('stepNothingToApply') }}</template>
                <template v-else>{{ $t('stepApplyHint') }}</template>
              </div>
            </div>
          </div>
        </div>

        <!-- Băng số liệu -->
        <div class="col-12">
          <div class="d-stats">
            <div class="d-stat">
              <h4>{{ $t('fileRows') }}</h4>
              <b>{{ current.totals.fileRows | numFormat('0,0') }}</b>
              <small>{{ current.totals.fileSegments | numFormat('0,0') }} {{ $t('segments') }}</small>
            </div>
            <div class="d-stat d-stat--ok">
              <h4>{{ $t('groupMatched') }}</h4>
              <b>{{ current.totals.matched | numFormat('0,0') }}</b>
              <small>{{ $t('groupMatchedHint') }}</small>
            </div>
            <div class="d-stat d-stat--danger">
              <h4>{{ $t('groupProviderFail') }}</h4>
              <b>{{ current.totals.providerFail | numFormat('0,0') }}</b>
              <small>{{ current.totals.refundAmount | numFormat('0,0') }}đ {{ $t('toRefund') }}</small>
            </div>
            <div class="d-stat d-stat--warn">
              <h4>{{ $t('groupProviderSuccess') }}</h4>
              <b>{{ current.totals.providerSuccess | numFormat('0,0') }}</b>
              <small>{{ $t('toCharge') }}</small>
            </div>
            <div class="d-stat">
              <h4>{{ $t('groupNotMatched') }}</h4>
              <b>{{ (current.totals.providerOnly + current.totals.systemOnly) | numFormat('0,0') }}</b>
              <small>{{ $t('providerOnlyShort') }} {{ current.totals.providerOnly }} · {{ $t('systemOnlyShort') }} {{ current.totals.systemOnly }}</small>
            </div>
          </div>
        </div>

        <!-- Ghi chú của kỳ -->
        <div class="col-12">
          <div class="tile">
            <reconcile-note :doc="current" api="/sms-delivery-reconcile" @saved="onNoteSaved"/>
          </div>
        </div>

        <!-- Bảng dòng -->
        <div class="col-12">
          <b-tabs v-model="tabIndex" @input="onTab">
            <b-tab :title="$t('tabCare')"></b-tab>
            <b-tab :title="$t('tabAds')"></b-tab>
          </b-tabs>
          <div class="tile" style="border-radius:0 0 6px 6px">
            <div class="form-inline mb-2">
              <label class="mr-1 mb-1">{{ $t('filterGroup') }}</label>
              <b-form-select class="mr-2 mb-1" v-model="filter.group" :options="groupOptions"
                             @change="loadLines(1)" style="width:290px"></b-form-select>
              <b-form-input class="mr-1 mb-1" v-model="filter.keyword" :placeholder="$t('searchPlaceholder')"
                            style="width:220px" @keydown.enter="loadLines(1)"></b-form-input>
              <button class="btn btn-secondary mr-1 mb-1" @click="loadLines(1)">{{ $t('search') }}</button>
              <button class="btn btn-outline-secondary is-border-1 mr-1 mb-1" @click="resetFilter">{{ $t('resetFilter') }}</button>
              <span class="ml-auto small" v-if="canSelectWholeGroup">
                <a href="javascript:;" @click="selectWholeGroup">{{ $t('selectWholeGroup', [lineCount]) }}</a>
              </span>
            </div>

            <div class="text-center text-gray-999 py-4" v-if="linesLoading">
              <b-spinner small></b-spinner>&nbsp;{{ $t('loading') }}
            </div>
            <div class="text-center text-gray-999 py-4" v-else-if="!lines.length">{{ $t('noLines') }}</div>

            <div class="table-responsive" v-else>
              <table class="table table-sm">
                <thead>
                <tr>
                  <th style="width:34px">
                    <input type="checkbox" :checked="allOnPageSelected" @change="toggleAllOnPage($event.target.checked)">
                  </th>
                  <th>{{ $t('colClient') }}</th>
                  <th>Brand</th>
                  <th>{{ $t('colNumber') }}</th>
                  <th>{{ $t('colTelco') }}</th>
                  <th>{{ $t('colSendTime') }}</th>
                  <th>Callio</th>
                  <th>{{ $t('colProvider') }}</th>
                  <th class="text-right">{{ $t('colSegments') }}</th>
                  <th class="text-right">{{ $t('colFee') }}</th>
                  <th>{{ $t('colMatchLevel') }}</th>
                  <th>{{ $t('colAction') }}</th>
                </tr>
                </thead>
                <tbody>
                <template v-for="item in lines">
                  <tr v-if="isGroupHead(item)" :key="'h-' + item._id" class="d-grouprow">
                    <td :colspan="12">
                      <span class="d-dot" :class="'d-dot--' + item.group"></span>
                      {{ groupLabel(item.group) }} · {{ groupCount(item.group) | numFormat('0,0') }} {{ $t('rowsUnit') }}
                      <span v-if="item.group === 'providerFail'" class="text-danger">
                        · {{ current.totals.refundAmount | numFormat('0,0') }}đ {{ $t('chargedFromClient') }}
                      </span>
                      <span v-else-if="item.group === 'providerSuccess'" class="text-warning">· {{ $t('toCharge') }}</span>
                      <span v-else-if="item.group === 'matched'" class="text-gray-999">· {{ $t('nothingToDo') }}</span>
                      <span v-else-if="item.group === 'providerOnly'" class="text-gray-999">· {{ $t('providerOnlyHint') }}</span>
                      <span v-else-if="item.group === 'systemOnly'" class="text-gray-999">· {{ $t('systemOnlyHint') }}</span>
                    </td>
                  </tr>
                  <tr :key="item._id">
                    <td>
                      <input type="checkbox" :disabled="!isSelectable(item)" :checked="!!selected[item._id]"
                             @change="toggleLine(item, $event.target.checked)">
                    </td>
                    <td>{{ item.clientName || '—' }}</td>
                    <td>{{ item.brandName || '—' }}</td>
                    <td>{{ item.number || '—' }}</td>
                    <td>{{ item.telco || '—' }}</td>
                    <td>{{ (item.providerTime || item.systemSendTime) | moment('DD/MM HH:mm') }}</td>
                    <td>
                      <span v-if="item.systemError === null" class="text-gray-999 small">{{ $t('noRecord') }}</span>
                      <span v-else class="badge" :class="item.systemError ? 'badge-danger' : 'badge-success'">
                        {{ item.systemError ? $t('failed') : $t('success') }}
                      </span>
                    </td>
                    <td>
                      <span v-if="!item.providerStatus" class="text-gray-999 small">{{ $t('noRow') }}</span>
                      <span v-else class="badge" :class="isProviderOk(item) ? 'badge-success' : 'badge-danger'">
                        {{ item.providerStatus }}<span v-if="item.providerError && item.providerError !== '0'"> ({{ item.providerError }})</span>
                      </span>
                    </td>
                    <td class="text-right">
                      <span :class="{'text-danger font-weight-bold': item.group === 'segmentDiff'}">
                        {{ item.systemSegments || 0 }} / {{ item.providerSegments || 0 }}
                      </span>
                    </td>
                    <td class="text-right">{{ item.chargedFee | numFormat('0,0') }}đ</td>
                    <td>
                      <span class="badge" :class="item.matchLevel === 'fuzzy' ? 'badge-warning' : 'badge-light'">
                        {{ matchLabel(item.matchLevel) }}
                      </span>
                    </td>
                    <td>
                      <span v-if="item.action === 'refund'" class="badge badge-danger">{{ $t('didRefund') }}</span>
                      <span v-else-if="item.action === 'charge'" class="badge badge-warning">{{ $t('didCharge') }}</span>
                      <span v-else-if="item.action === 'skipped'" class="small text-gray-999" :title="item.actionNote">{{ $t('skipped') }}</span>
                      <span v-else-if="item.group === 'providerFail'" class="badge badge-outline-danger">{{ $t('suggestRefund') }}</span>
                      <span v-else-if="item.group === 'providerSuccess'" class="badge badge-outline-warning">{{ $t('suggestCharge') }}</span>
                      <span v-else class="text-gray-999">—</span>
                    </td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>

            <div class="c-paging clearfix" v-if="lineCount > filter.pageSize">
              <div class="c-paging__count">{{ $t('found', [lineCount]) }}</div>
              <b-pagination class="pull-right mb-0" size="sm" v-model="filter.page" :total-rows="lineCount"
                            :per-page="filter.pageSize" @input="loadLines()"></b-pagination>
            </div>
          </div>
        </div>
      </div>

      <!-- Thanh hành động -->
      <div class="d-sms-bar" v-if="current && selectedCount">
        <span>{{ $t('selectedLines', [selectedCount]) }}</span>
        <span>{{ $t('toRefund') }}: <b>{{ selectedRefund | numFormat('0,0') }}đ</b></span>
        <span>{{ $t('toCharge') }}: <b>{{ selectedChargeCount }}</b> {{ $t('rowsUnit') }}
          <span class="small" style="opacity:.75">({{ $t('amountAtApply') }})</span></span>
        <span class="ml-auto">
          <button class="btn btn-sm btn-secondary mr-1" @click="clearSelection">{{ $t('clearSelection') }}</button>
          <button class="btn btn-sm btn-success" @click="openConfirm" v-if="canApply">{{ $t('applyBtn') }}</button>
        </span>
      </div>
    </template>

    <!-- Hộp TẠO KỲ -->
    <b-modal v-model="showCreate" :title="$t('createPeriod')" size="lg" @ok.prevent="doCreate"
             :ok-title="$t('createBtn')" :cancel-title="$t('cancel')" :ok-disabled="uploading">
      <div class="form-row d-create">
        <div class="form-group col-md-5">
          <label>{{ $t('provider') }} <span class="text-danger">*</span></label>
          <b-form-select v-model="createForm.gateway" :options="gatewayOptionsRequired"></b-form-select>
          <small class="text-gray-999">{{ $t('providerHint') }}</small>
        </div>
        <div class="form-group col-md-7">
          <label>{{ $t('timeRange') }} <span class="text-danger">*</span></label>
          <date-picker v-model="createForm.range" type="date" range value-type="timestamp" format="DD/MM/YYYY"
                       :lang="$i18n.locale" :clearable="false" class="w-100"></date-picker>
          <small class="text-gray-999">{{ $t('timeRangeHint') }}</small>
        </div>
      </div>

      <div class="d-drop" :class="{'d-drop--over': dragOver, 'd-drop--busy': uploading}"
           @dragover.prevent="dragOver = true" @dragleave.prevent="dragOver = false"
           @drop.prevent="onDrop" @click="pickFile">
        <input ref="fileInput" type="file" accept=".xlsx,.xls" hidden @change="onPick">
        <div class="d-drop__icon">📄</div>
        <p class="mb-1"><b>{{ createForm.file ? createForm.file.name : $t('dropTitle') }}</b></p>
        <p class="small mb-1">{{ $t('dropHint') }}</p>
        <p class="small text-gray-999 mb-0">{{ $t('fileHint') }}</p>
      </div>

      <div class="form-group mt-3 mb-0">
        <label>{{ $t('filePassword') }}</label>
        <b-form-input v-model="createForm.password" type="password" :placeholder="$t('filePasswordHint')"></b-form-input>
      </div>

      <div v-if="uploading" class="alert alert-warning mt-3 mb-0">
        <b-spinner small></b-spinner>&nbsp;{{ $t('uploadingNotice') }}
      </div>
    </b-modal>

    <!-- Hộp XÁC NHẬN ÁP DỤNG -->
    <b-modal v-model="showConfirm" :title="$t('confirmTitle')" @ok.prevent="doApply" :ok-title="$t('confirmOk')"
             :cancel-title="$t('cancel')" ok-variant="danger"
             :ok-disabled="applying || confirmText.trim().toUpperCase() !== 'DONG Y'">
      <table class="table table-sm mb-2">
        <tbody>
        <tr><td>{{ $t('refundLines') }}</td><td class="text-right"><b>{{ selectedRefundCount }}</b></td></tr>
        <tr><td>{{ $t('refundTotal') }}</td><td class="text-right text-danger"><b>{{ selectedRefund | numFormat('0,0') }}đ</b></td></tr>
        <tr><td>{{ $t('chargeLines') }}</td><td class="text-right"><b>{{ selectedChargeCount }}</b></td></tr>
        <tr><td>{{ $t('chargeTotal') }}</td><td class="text-right text-gray-999">{{ $t('amountAtApply') }}</td></tr>
        <tr><td>{{ $t('affectedClients') }}</td><td class="text-right"><b>{{ affectedClients.join(', ') || '—' }}</b></td></tr>
        </tbody>
      </table>
      <b-alert show variant="danger" class="small">{{ $t('confirmWarning') }}</b-alert>
      <label class="small mb-1">{{ $t('confirmTypeHint') }}</label>
      <b-form-input v-model="confirmText" placeholder="DONG Y"></b-form-input>
      <div v-if="applying" class="alert alert-warning mt-2 mb-0">
        <b-spinner small></b-spinner>&nbsp;{{ $t('applyingNotice') }}
      </div>
    </b-modal>
  </main>
</template>

<script>
import moment from 'moment';
import numeral from 'numeral';
import DatePicker from 'vue2-datepicker';
import ReconcileNote from '@/components/ReconcileNote.vue';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/vi';

export default {
  components: {DatePicker, ReconcileNote},
  data() {
    return {
      // màn danh sách
      periods: [],
      filterGateway: '',
      filterStatus: '',
      loading: false,
      // màn chi tiết
      currentId: null,
      current: null,
      tabIndex: 0,
      lines: [],
      lineCount: 0,
      linesLoading: false,
      selected: {},
      applyWholeGroup: false,
      filter: {group: '', keyword: '', page: 1, pageSize: 50},
      // dùng chung
      gateways: [],
      showCreate: false,
      showConfirm: false,
      confirmText: '',
      uploading: false,
      exporting: false,
      applying: false,
      deleting: false,
      dragOver: false,
      createForm: {
        gateway: '', file: null, password: '',
        range: [moment().subtract(7, 'days').startOf('day').valueOf(), moment().endOf('day').valueOf()],
      },
    };
  },
  computed: {
    gatewayOptions() {
      return [{value: '', text: this.$t('allProviders')}, ...this.gateways.map(g => ({value: g._id, text: g.name}))];
    },
    gatewayOptionsRequired() {
      return [{value: '', text: this.$t('selectProvider')}, ...this.gateways.map(g => ({value: g._id, text: g.name}))];
    },
    statusOptions() {
      return [
        {value: '', text: this.$t('allStatus')},
        {value: 'none', text: this.$t('notApplied')},
        {value: 'applied', text: this.$t('applied')},
      ];
    },
    groupOptions() {
      const t = this.current ? this.current.totals : {};
      return [
        {value: '', text: this.$t('allGroups')},
        {value: 'providerFail', text: `${this.$t('groupProviderFail')} (${t.providerFail || 0})`},
        {value: 'providerSuccess', text: `${this.$t('groupProviderSuccess')} (${t.providerSuccess || 0})`},
        {value: 'segmentDiff', text: `${this.$t('groupSegmentDiff')} (${t.segmentDiff || 0})`},
        {value: 'matched', text: `${this.$t('groupMatched')} (${t.matched || 0})`},
        {value: 'providerOnly', text: `${this.$t('groupProviderOnly')} (${t.providerOnly || 0})`},
        {value: 'systemOnly', text: `${this.$t('groupSystemOnly')} (${t.systemOnly || 0})`},
      ];
    },
    hasApplied() {
      if (!this.current) return false;
      const t = this.current.totals;
      return this.current.status === 'applied' || (t.appliedRefund || 0) > 0 || (t.appliedCharge || 0) > 0;
    },
    // Số dòng còn phải quyết định. Lấy từ tổng của kỳ trừ đi số đã xử lý (đếm ở back-end qua `pending`).
    needAction() {
      if (!this.current) return 0;
      const t = this.current.totals;
      if (typeof t.pendingAction === 'number') return t.pendingAction;
      return (t.providerFail || 0) + (t.providerSuccess || 0);
    },
    selectedList() {
      return Object.values(this.selected);
    },
    selectedCount() {
      return this.selectedList.length;
    },
    selectedRefundCount() {
      return this.selectedList.filter(l => l.group === 'providerFail').length;
    },
    selectedChargeCount() {
      return this.selectedList.filter(l => l.group === 'providerSuccess').length;
    },
    selectedRefund() {
      return this.selectedList.filter(l => l.group === 'providerFail').reduce((s, l) => s + (l.chargedFee || 0), 0);
    },
    affectedClients() {
      return [...new Set(this.selectedList.map(l => l.clientName).filter(Boolean))].slice(0, 6);
    },
    allOnPageSelected() {
      const selectable = this.lines.filter(l => this.isSelectable(l));
      return selectable.length > 0 && selectable.every(l => this.selected[l._id]);
    },
    canApply() {
      return ['owner', 'admin', 'tech', 'account'].includes(this.$store.state.user.role)
          && (this.selectedRefundCount + this.selectedChargeCount) > 0;
    },
    canSelectWholeGroup() {
      return ['providerFail', 'providerSuccess'].includes(this.filter.group) && this.lineCount > 0;
    },
  },
  watch: {
    // Vào/ra một kỳ bằng đường dẫn — nút Back của trình duyệt cũng chạy đúng.
    '$route.params.id': {
      immediate: true,
      handler(id) {
        this.currentId = id || null;
        this.selected = {};
        this.applyWholeGroup = false;
        if (id) this.loadPeriod(id);
        else this.loadList();
      },
    },
  },
  mounted() {
    this.loadGateways();
  },
  methods: {
    fmt(v) {
      return numeral(v || 0).format('0,0');
    },
    async loadGateways() {
      try {
        const {data} = await this.$service.get('/sms-gateway', {params: {pageSize: 100}});
        this.gateways = (data && data.docs) || [];
      } catch (e) {
        this.gateways = [];
      }
    },
    // ── Danh sách kỳ ────────────────────────────────────────────────────────
    async loadList() {
      this.loading = true;
      try {
        const params = {pageSize: 50};
        if (this.filterGateway) params.gateway = this.filterGateway;
        if (this.filterStatus) params.status = this.filterStatus;
        const {data} = await this.$service.get('/sms-delivery-reconcile', {params});
        this.periods = (data && data.docs) || [];
      } catch (e) {
        this.periods = [];
        this.$swal({text: this.errMsg(e, this.$t('loadFailed')), icon: 'error'});
      } finally {
        this.loading = false;
      }
    },
    openPeriod(id) {
      this.$router.push(`/report/sms-delivery-reconcile/${id}`);
    },
    backToList() {
      this.$router.push('/report/sms-delivery-reconcile');
    },
    // ── Tạo kỳ ──────────────────────────────────────────────────────────────
    openCreate() {
      this.createForm.file = null;
      this.createForm.password = '';
      if (!this.createForm.gateway) this.createForm.gateway = this.filterGateway || '';
      this.showCreate = true;
    },
    pickFile() {
      if (this.uploading) return;
      this.$refs.fileInput.click();
    },
    onPick(e) {
      const f = e.target.files && e.target.files[0];
      if (f) this.$set(this.createForm, 'file', f);
    },
    onDrop(e) {
      this.dragOver = false;
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (!f) return;
      const ext = String(f.name || '').toLowerCase().split('.').pop();
      if (!['xlsx', 'xls'].includes(ext)) {
        this.$swal({text: this.$t('wrongFileType'), icon: 'error'});
        return;
      }
      this.$set(this.createForm, 'file', f);
    },
    async doCreate() {
      if (!this.createForm.gateway) {
        this.$swal({text: this.$t('selectProviderFirst'), icon: 'error'});
        return;
      }
      if (!this.createForm.file) {
        this.$swal({text: this.$t('selectFileFirst'), icon: 'error'});
        return;
      }
      const form = new FormData();
      form.append('file', this.createForm.file);
      form.append('gateway', this.createForm.gateway);
      form.append('from', String(this.createForm.range[0]));
      form.append('to', String(moment(this.createForm.range[1]).endOf('day').valueOf()));
      if (this.createForm.password) form.append('password', this.createForm.password);

      this.uploading = true;
      try {
        const {data} = await this.$service.post('/sms-delivery-reconcile/upload', form,
            {headers: {'Content-Type': 'multipart/form-data'}, timeout: 10 * 60 * 1000});
        this.showCreate = false;
        const notes = [];
        if (data.skippedOutOfRange) notes.push(this.$t('skippedOutOfRange', [data.skippedOutOfRange]));
        if (data.duplicateOf) notes.push(this.$t('duplicatePeriod', [data.duplicateOf.fileName || '']));
        if (notes.length) await this.$swal({text: notes.join('\n'), icon: 'warning'});
        // Tạo xong đi thẳng vào trong kỳ — đó là việc tiếp theo người dùng phải làm.
        this.openPeriod(data.data._id);
      } catch (e) {
        this.$swal({text: this.errMsg(e, this.$t('uploadFailed')), icon: 'error'});
      } finally {
        this.uploading = false;
      }
    },
    // ── Chi tiết kỳ ─────────────────────────────────────────────────────────
    onNoteSaved(n) {
      this.current = {...this.current, ...n};
    },
    async loadPeriod(id) {
      this.current = null;
      try {
        const {data} = await this.$service.get(`/sms-delivery-reconcile/${id}`);
        this.current = data.data;
        // Mở kỳ ra là vào thẳng việc cần làm: nhóm cần hoàn tiền, không có thì nhóm cần truy thu.
        this.filter.group = this.current.totals.providerFail ? 'providerFail'
            : (this.current.totals.providerSuccess ? 'providerSuccess' : '');
        await this.loadLines(1);
      } catch (e) {
        this.$swal({text: this.errMsg(e, this.$t('loadFailed')), icon: 'error'});
        this.backToList();
      }
    },
    onTab() {
      this.loadLines(1);
    },
    resetFilter() {
      this.filter.group = '';
      this.filter.keyword = '';
      this.applyWholeGroup = false;
      this.loadLines(1);
    },
    async loadLines(page) {
      if (!this.current) return;
      if (page) this.filter.page = page;
      this.linesLoading = true;
      try {
        const params = {
          page: this.filter.page,
          pageSize: this.filter.pageSize,
          smsType: this.tabIndex === 0 ? 'care' : 'ads',
        };
        if (this.filter.group) params.group = this.filter.group;
        if (this.filter.keyword) params.keyword = this.filter.keyword;
        const {data} = await this.$service.get(`/sms-delivery-reconcile/${this.current._id}/lines`, {params});
        this.lines = (data && data.docs) || [];
        this.lineCount = (data && data.totalDocs) || 0;
      } catch (e) {
        this.lines = [];
        this.lineCount = 0;
        this.$swal({text: this.errMsg(e, this.$t('loadFailed')), icon: 'error'});
      } finally {
        this.linesLoading = false;
      }
    },
    isProviderOk(item) {
      return /^(sent|success|delivered)$/i.test(String(item.providerStatus || '').trim());
    },
    isSelectable(item) {
      return !item.action && ['providerFail', 'providerSuccess'].includes(item.group) && !!item.sms;
    },
    matchLabel(level) {
      if (level === 'bid') return this.$t('matchBid');
      if (level === 'msgid') return this.$t('matchMsgId');
      if (level === 'fuzzy') return this.$t('matchFuzzy');
      return '—';
    },
    isGroupHead(item) {
      const idx = this.lines.indexOf(item);
      return idx === 0 || this.lines[idx - 1].group !== item.group;
    },
    groupLabel(g) {
      const map = {
        matched: this.$t('groupMatched'), providerFail: this.$t('groupProviderFail'),
        providerSuccess: this.$t('groupProviderSuccess'), segmentDiff: this.$t('groupSegmentDiff'),
        providerOnly: this.$t('groupProviderOnly'), systemOnly: this.$t('groupSystemOnly'),
      };
      return map[g] || g;
    },
    groupCount(g) {
      return this.current && this.current.totals ? (this.current.totals[g] || 0) : 0;
    },
    toggleLine(item, checked) {
      if (checked) this.$set(this.selected, item._id, item);
      else this.$delete(this.selected, item._id);
    },
    toggleAllOnPage(checked) {
      this.lines.filter(l => this.isSelectable(l)).forEach(l => this.toggleLine(l, checked));
    },
    selectWholeGroup() {
      this.$swal({
        text: this.$t('selectWholeGroupConfirm', [this.lineCount]),
        icon: 'question',
        showCancelButton: true,
      }).then(r => {
        if (r.isConfirmed) {
          this.applyWholeGroup = true;
          this.openConfirm();
        }
      });
    },
    clearSelection() {
      this.selected = {};
      this.applyWholeGroup = false;
    },
    openConfirm() {
      this.confirmText = '';
      this.showConfirm = true;
    },
    async doApply() {
      this.applying = true;
      try {
        const body = this.applyWholeGroup ? {group: this.filter.group} : {lineIds: Object.keys(this.selected)};
        const {data} = await this.$service.post(`/sms-delivery-reconcile/${this.current._id}/apply`, body,
            {timeout: 10 * 60 * 1000});
        this.showConfirm = false;
        const d = data.data;
        let msg = this.$t('applyDone', [d.refundCount, this.fmt(d.refundAmount), d.chargeCount, this.fmt(d.chargeAmount), d.skipCount]);
        if (d.remaining) msg += ' ' + this.$t('applyRemaining', [d.remaining]);
        this.$swal({icon: 'success', text: msg});
        this.clearSelection();
        await this.loadPeriod(this.current._id);
      } catch (e) {
        this.$swal({text: this.errMsg(e, this.$t('applyFailed')), icon: 'error'});
      } finally {
        this.applying = false;
      }
    },
    // ── Xoá kỳ ──────────────────────────────────────────────────────────────
    async removePeriod(p, backAfter) {
      const applied = p.status === 'applied';
      const r = await this.$swal({
        title: this.$t('deletePeriod'),
        html: this.$t('deletePeriodConfirm', [p.fileName || ''])
            + (applied ? `<br><br><b class="text-danger">${this.$t('deleteAppliedWarn')}</b>` : ''),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('deletePeriod'),
        cancelButtonText: this.$t('cancel'),
      });
      if (!r.isConfirmed) return;
      this.deleting = true;
      try {
        await this.$service.delete(`/sms-delivery-reconcile/${p._id}`);
        if (backAfter) this.backToList();
        else await this.loadList();
      } catch (e) {
        this.$swal({text: this.errMsg(e, this.$t('deleteFailed')), icon: 'error'});
      } finally {
        this.deleting = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const res = await this.$service.get(`/sms-delivery-reconcile/${this.current._id}/export`, {responseType: 'blob'});
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `doi-soat-trang-thai-sms-${moment(this.current.from).format('YYYYMMDD')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$swal({text: this.errMsg(e, this.$t('exportFailed')), icon: 'error'});
      } finally {
        this.exporting = false;
      }
    },
    errMsg(err, fallback) {
      if (err && err.response && err.response.data && err.response.data.message) return err.response.data.message;
      return (err && err.message) || fallback;
    },
  },
  i18n: {
    messages: {
      vi: {
        pageTitle: 'Đối soát trạng thái tin nhắn',
        createPeriod: 'Tạo kỳ đối soát', createBtn: 'Tạo kỳ & so khớp',
        listNote: 'Mỗi lần tải file kết quả của nhà cung cấp lên là một KỲ ĐỐI SOÁT. Mở kỳ ra để xem chênh lệch theo từng tin rồi hoàn tiền hoặc truy thu cho khách.',
        provider: 'Nhà cung cấp', allProviders: 'Tất cả nhà cung cấp', selectProvider: '-- Chọn nhà cung cấp --',
        providerHint: 'Mỗi file thuộc một nhà cung cấp',
        status: 'Trạng thái', allStatus: 'Tất cả trạng thái',
        timeRange: 'Khoảng thời gian', timeRangeHint: 'Tính theo thời gian GỬI của tin',
        reload: 'Tải lại', loading: 'Đang tải…',
        emptyTitle: 'Chưa có kỳ đối soát nào',
        emptyHint: 'Tạo kỳ đầu tiên: chọn nhà cung cấp, khoảng thời gian rồi tải file kết quả của họ lên.',
        colPeriod: 'Kỳ đối soát', colFile: 'File', colFileRows: 'Dòng trong file',
        colNeedRefund: 'Cần hoàn', colNeedCharge: 'Cần truy thu', colCreatedBy: 'Người tạo', colNote: 'Ghi chú', colActions: 'Thao tác',
        openPeriod: 'Mở', backToList: 'Về danh sách kỳ',
        applied: 'Đã áp dụng', notApplied: 'Chưa áp dụng',
        stepMatched: 'Đã so khớp', rowsFromFile: 'dòng từ file',
        stepPick: 'Chọn dòng cần xử lý', stepPickHint: 'còn {0} dòng cần quyết định', stepPickDone: 'không còn dòng nào',
        stepApply: 'Hoàn tiền / Truy thu', stepApplyHint: 'tick dòng rồi bấm Áp dụng điều chỉnh',
        stepNothingToApply: 'kỳ này không có gì phải điều chỉnh',
        stepApplyDone: 'đã hoàn {0}đ · đã truy thu {1}đ',
        fileRows: 'Dòng trong file', segments: 'tin (segment)',
        groupMatched: 'Khớp', groupMatchedHint: '2 phía cùng thành công',
        groupProviderFail: 'Callio báo thành công — NCC báo hỏng',
        groupProviderSuccess: 'Callio báo lỗi — NCC gửi được',
        groupSegmentDiff: 'Lệch số tin', groupProviderOnly: 'Chỉ có ở nhà cung cấp', groupSystemOnly: 'Chỉ có ở Callio',
        groupNotMatched: 'Không khớp', providerOnlyShort: 'NCC', systemOnlyShort: 'Callio',
        toRefund: 'cần hoàn', toCharge: 'cần truy thu',
        tabCare: 'Tin CSKH', tabAds: 'Tin quảng cáo',
        filterGroup: 'Lọc nhóm', allGroups: 'Tất cả', searchPlaceholder: 'Tìm số điện thoại / mã tin…',
        search: 'Tìm', resetFilter: 'Xoá lọc', noLines: 'Không có dòng nào',
        selectWholeGroup: 'Chọn cả nhóm ({0} dòng)',
        selectWholeGroupConfirm: 'Áp dụng cho cả nhóm gồm {0} dòng? Các dòng khớp mờ sẽ bị bỏ qua, phải tick tay.',
        rowsUnit: 'tin', chargedFromClient: 'đã thu của khách', nothingToDo: 'không cần làm gì',
        providerOnlyHint: 'Callio không có bản ghi — không động vào tiền',
        systemOnlyHint: 'file không có dòng tương ứng — cần xem lại',
        colClient: 'Khách hàng', colNumber: 'Số nhận', colTelco: 'Nhà mạng', colSendTime: 'Thời gian gửi',
        colProvider: 'Nhà cung cấp', colSegments: 'Tin (Callio/NCC)', colFee: 'Tiền đã trừ',
        colMatchLevel: 'Khớp bằng', colAction: 'Xử lý',
        noRecord: 'Không có bản ghi', noRow: 'Không có dòng', failed: 'Lỗi', success: 'Thành công',
        matchBid: 'Mã tin', matchMsgId: 'Mã NCC', matchFuzzy: 'Khớp mờ',
        didRefund: 'Đã hoàn tiền', didCharge: 'Đã truy thu', skipped: 'Bỏ qua',
        suggestRefund: 'Hoàn tiền', suggestCharge: 'Truy thu',
        selectedLines: 'Đã chọn {0} dòng', clearSelection: 'Bỏ chọn', applyBtn: 'Áp dụng điều chỉnh',
        amountAtApply: 'số tiền tính khi áp dụng, theo đơn giá của brand',
        dropTitle: 'Kéo thả file kết quả của nhà cung cấp vào đây',
        dropHint: 'hoặc bấm để chọn file — chấp nhận .xlsx, .csv; mỗi dòng là một tin nhắn',
        fileHint: 'File phải có cột trạng thái (Status) và một trong các cột Bid / MessageID / Mobile.',
        wrongFileType: 'Chỉ nhận file .xlsx hoặc .xls',
        filePassword: 'Mật khẩu mở file (nếu có)', filePasswordHint: 'Để trống nếu file không đặt mật khẩu',
        cancel: 'Huỷ', uploadingNotice: 'Đang đọc file và so khớp…',
        selectProviderFirst: 'Vui lòng chọn nhà cung cấp', selectFileFirst: 'Vui lòng chọn file',
        skippedOutOfRange: 'Đã bỏ qua {0} dòng nằm ngoài khoảng thời gian đã chọn.',
        duplicatePeriod: 'Khoảng thời gian này đã từng đối soát trước đó (file: {0}). Bản vừa tải lên là kỳ MỚI, nằm riêng.',
        uploadFailed: 'Không tạo được kỳ đối soát', loadFailed: 'Không tải được dữ liệu',
        exportReport: 'Xuất biên bản', exportFailed: 'Không xuất được biên bản', applyFailed: 'Không áp dụng được',
        deletePeriod: 'Xoá kỳ', deleteFailed: 'Không xoá được kỳ đối soát',
        deletePeriodConfirm: 'Xoá kỳ đối soát này (file {0})?',
        deleteAppliedWarn: 'Kỳ này đã áp dụng điều chỉnh. Xoá kỳ KHÔNG huỷ các giao dịch hoàn tiền / truy thu đã sinh.',
        confirmTitle: 'Xác nhận điều chỉnh tiền', confirmOk: 'Áp dụng',
        refundLines: 'Số tin hoàn tiền', refundTotal: 'Tổng tiền hoàn',
        chargeLines: 'Số tin truy thu', chargeTotal: 'Tổng truy thu',
        affectedClients: 'Khách hàng bị ảnh hưởng',
        confirmWarning: 'Giao dịch ví KHÔNG hoàn tác được. Khách sẽ thấy ngay khoản này trong lịch sử giao dịch.',
        confirmTypeHint: 'Gõ DONG Y để xác nhận:',
        applyingNotice: 'Đang sinh giao dịch…',
        applyDone: 'Đã hoàn {0} tin ({1}đ), truy thu {2} tin ({3}đ), bỏ qua {4} tin.',
        applyRemaining: 'Còn {0} dòng chưa xử lý, bấm Áp dụng lần nữa để chạy tiếp.',
        found: 'Tìm thấy {0}',
      },
      en: {
        pageTitle: 'Delivery status reconciliation',
        createPeriod: 'New reconciliation', createBtn: 'Create & match',
        listNote: 'Each provider file you upload becomes a RECONCILIATION PERIOD. Open one to review per-message differences and refund or charge clients.',
        provider: 'Provider', allProviders: 'All providers', selectProvider: '-- Select provider --',
        providerHint: 'Each file belongs to one provider',
        status: 'Status', allStatus: 'All statuses',
        timeRange: 'Time range', timeRangeHint: 'Based on message SEND time',
        reload: 'Reload', loading: 'Loading…',
        emptyTitle: 'No reconciliation yet',
        emptyHint: 'Create the first one: pick a provider, a time range and upload their result file.',
        colPeriod: 'Period', colFile: 'File', colFileRows: 'Rows in file',
        colNeedRefund: 'To refund', colNeedCharge: 'To charge', colCreatedBy: 'Created by', colNote: 'Note', colActions: 'Actions',
        openPeriod: 'Open', backToList: 'Back to list',
        applied: 'Applied', notApplied: 'Not applied',
        stepMatched: 'Matched', rowsFromFile: 'rows from file',
        stepPick: 'Pick rows to handle', stepPickHint: '{0} rows still need a decision', stepPickDone: 'nothing left',
        stepApply: 'Refund / Charge', stepApplyHint: 'tick rows then press Apply adjustments',
        stepNothingToApply: 'nothing to adjust in this period',
        stepApplyDone: 'refunded {0}đ · charged {1}đ',
        fileRows: 'Rows in file', segments: 'segments',
        groupMatched: 'Matched', groupMatchedHint: 'both sides successful',
        groupProviderFail: 'Callio OK — provider failed',
        groupProviderSuccess: 'Callio failed — provider sent',
        groupSegmentDiff: 'Segment mismatch', groupProviderOnly: 'Provider only', groupSystemOnly: 'Callio only',
        groupNotMatched: 'Not matched', providerOnlyShort: 'provider', systemOnlyShort: 'Callio',
        toRefund: 'to refund', toCharge: 'to charge',
        tabCare: 'Care SMS', tabAds: 'Ads SMS',
        filterGroup: 'Group', allGroups: 'All', searchPlaceholder: 'Search phone / message id…',
        search: 'Search', resetFilter: 'Clear filter', noLines: 'No rows',
        selectWholeGroup: 'Select whole group ({0} rows)',
        selectWholeGroupConfirm: 'Apply to the whole group of {0} rows? Fuzzy-matched rows are skipped.',
        rowsUnit: 'messages', chargedFromClient: 'charged to clients', nothingToDo: 'nothing to do',
        providerOnlyHint: 'no Callio record — money untouched',
        systemOnlyHint: 'no matching row in the file — needs a look',
        colClient: 'Client', colNumber: 'To', colTelco: 'Carrier', colSendTime: 'Sent at',
        colProvider: 'Provider', colSegments: 'Segments (Callio/provider)', colFee: 'Charged',
        colMatchLevel: 'Matched by', colAction: 'Action',
        noRecord: 'No record', noRow: 'No row', failed: 'Failed', success: 'Success',
        matchBid: 'Bid', matchMsgId: 'Message id', matchFuzzy: 'Fuzzy',
        didRefund: 'Refunded', didCharge: 'Charged', skipped: 'Skipped',
        suggestRefund: 'Refund', suggestCharge: 'Charge',
        selectedLines: '{0} rows selected', clearSelection: 'Clear', applyBtn: 'Apply adjustments',
        amountAtApply: 'computed on apply, from the brand price',
        dropTitle: 'Drop the provider result file here',
        dropHint: 'or click to choose a file — .xlsx or .csv, one row per message',
        fileHint: 'The file needs a Status column and one of Bid / MessageID / Mobile.',
        wrongFileType: 'Only .xlsx or .xls files are accepted',
        filePassword: 'File password (if any)', filePasswordHint: 'Leave empty if the file is not protected',
        cancel: 'Cancel', uploadingNotice: 'Reading file and matching…',
        selectProviderFirst: 'Please select a provider', selectFileFirst: 'Please select a file',
        skippedOutOfRange: 'Skipped {0} rows outside the selected time range.',
        duplicatePeriod: 'This time range was reconciled before (file: {0}). The upload created a NEW, separate period.',
        uploadFailed: 'Could not create the period', loadFailed: 'Could not load data',
        exportReport: 'Export', exportFailed: 'Export failed', applyFailed: 'Could not apply',
        deletePeriod: 'Delete', deleteFailed: 'Could not delete the period',
        deletePeriodConfirm: 'Delete this reconciliation period (file {0})?',
        deleteAppliedWarn: 'This period was already applied. Deleting it does NOT reverse the refund / charge transactions.',
        confirmTitle: 'Confirm money adjustment', confirmOk: 'Apply',
        refundLines: 'Messages to refund', refundTotal: 'Total refund',
        chargeLines: 'Messages to charge', chargeTotal: 'Total charge',
        affectedClients: 'Affected clients',
        confirmWarning: 'Wallet transactions CANNOT be undone. Clients see them immediately.',
        confirmTypeHint: 'Type DONG Y to confirm:',
        applyingNotice: 'Creating transactions…',
        applyDone: 'Refunded {0} messages ({1}đ), charged {2} messages ({3}đ), skipped {4}.',
        applyRemaining: '{0} rows left, press Apply again to continue.',
        found: 'Found {0}',
      },
    },
  },
};
</script>

<style scoped>
.text-gray-999 { color: #999; }
.d-back { color: inherit; margin-right: 6px; }
.d-create label, .d-create small { display: block; }
.d-create .mx-datepicker { display: block; width: 100%; }
.d-rowlink { cursor: pointer; }

.d-empty { text-align: center; padding: 40px 20px; }
.d-empty__icon { font-size: 40px; line-height: 1; margin-bottom: 6px; }

.d-steps { display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; }
.d-step {
  flex: 1; min-width: 190px; border: 1px solid #e6e9ee; border-radius: 6px;
  padding: 10px 12px; background: #fff; font-size: 13px; color: #8a94a6;
}
.d-step.on { border-color: #1e88e5; color: #23272f; font-weight: 600; background: #f3f8fe; }
.d-step.done { border-color: #b7e0c3; color: #23272f; background: #f4fbf6; }
.d-step span {
  display: inline-block; width: 20px; height: 20px; line-height: 20px; border-radius: 50%;
  background: #dfe4ea; color: #5b6472; text-align: center; font-size: 12px; margin-right: 7px;
}
.d-step.on span { background: #1e88e5; color: #fff; }
.d-step.done span { background: #34a853; color: #fff; }
.d-step .small { color: #8a94a6; margin-top: 2px; padding-left: 27px; }

.d-drop {
  border: 2px dashed #cfd6e0; border-radius: 6px; padding: 26px; text-align: center;
  color: #8a94a6; background: #fafbfc; cursor: pointer;
}
.d-drop--over { border-color: #1e88e5; background: #f3f8fe; }
.d-drop--busy { opacity: .6; cursor: default; }
.d-drop__icon { font-size: 34px; line-height: 1; }

.d-stats { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 14px; }
@media (max-width: 991px) { .d-stats { grid-template-columns: repeat(2, 1fr); } }
.d-stat {
  background: #fff; border: 1px solid #e6e9ee; border-left: 4px solid #b0b8c4;
  border-radius: 6px; padding: 12px 14px; color: #23272f;
}
.d-stat h4 {
  margin: 0; font-size: 12px; font-weight: 600; color: #8a94a6;
  text-transform: uppercase; letter-spacing: .02em;
}
.d-stat b { font-size: 21px; display: block; margin-top: 4px; }
.d-stat small { color: #8a94a6; font-size: 12px; }
.d-stat--ok { border-left-color: #34a853; }
.d-stat--danger { border-left-color: #e05252; }
.d-stat--warn { border-left-color: #f5a623; }

.d-grouprow td { background: #f7f9fc; font-weight: 600; border-top: 2px solid #e6e9ee; }
.d-dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: #b0b8c4; margin-right: 6px; }
.d-dot--providerFail { background: #e05252; }
.d-dot--providerSuccess { background: #f5a623; }
.d-dot--segmentDiff { background: #f5a623; }
.d-dot--matched { background: #34a853; }

.d-sms-bar {
  position: sticky; bottom: 0; background: #2f3542; color: #fff; padding: 10px 16px;
  display: flex; gap: 18px; align-items: center; flex-wrap: wrap; font-size: 13px;
  border-radius: 6px; margin-top: 8px; z-index: 10;
}
.badge-outline-danger { border: 1px solid #dc3545; color: #dc3545; background: transparent; }
.badge-outline-warning { border: 1px solid #f0ad4e; color: #b9770e; background: transparent; }
</style>
